import { supabase } from "@/lib/supabaseClient";

export type AppProfile = {
  id: string;
  fullName: string | null;
  role: string;
  organizationId: string;
  avatarUrl: string | null;
};

export type OAuthProvider = "google" | "apple" | "facebook";

export async function signIn(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
}

export async function signUp(
  email: string,
  password: string,
  meta?: { referralCode?: string; fullName?: string }
) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: meta
      ? { data: { referral_code: meta.referralCode ?? "", full_name: meta.fullName ?? "" } }
      : undefined,
  });
  if (error) throw error;
  return data;
}

export async function signInWithOAuth(provider: OAuthProvider) {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: `${window.location.origin}/app/dashboard`,
    },
  });
  if (error) throw error;
  // El navegador es redirigido al proveedor; no hay sesión todavía en este punto.
  return data;
}

export async function signOut() {
  await supabase.auth.signOut();
}

export async function getSession() {
  const { data } = await supabase.auth.getSession();
  return data.session;
}

export async function fetchCurrentProfile(): Promise<AppProfile | null> {
  const { data: userData } = await supabase.auth.getUser();
  const user = userData.user;
  if (!user) return null;

  const { data, error } = await supabase
    .from("profiles")
    .select("id, full_name, role, organization_id, avatar_url")
    .eq("id", user.id)
    .maybeSingle();

  if (error || !data) return null;

  return {
    id: data.id,
    fullName: data.full_name,
    role: data.role,
    organizationId: data.organization_id,
    avatarUrl: data.avatar_url,
  };
}

/**
 * Sube una nueva foto de perfil al bucket público "avatars" (una carpeta
 * por usuario, siempre el mismo nombre de archivo para que se sobrescriba
 * en vez de acumular versiones viejas), actualiza profiles.avatar_url y
 * devuelve la URL pública final (con un parámetro de caché distinto cada
 * vez, para que el navegador no siga mostrando la foto anterior).
 */
export async function uploadAvatar(file: File): Promise<string> {
  const { data: userData } = await supabase.auth.getUser();
  const user = userData.user;
  if (!user) throw new Error("Debes iniciar sesión para cambiar tu foto.");

  const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
  const path = `${user.id}/avatar.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from("avatars")
    .upload(path, file, { upsert: true, cacheControl: "3600" });
  if (uploadError) throw uploadError;

  const { data: publicUrlData } = supabase.storage.from("avatars").getPublicUrl(path);
  const avatarUrl = `${publicUrlData.publicUrl}?t=${Date.now()}`;

  const { error: updateError } = await supabase
    .from("profiles")
    .update({ avatar_url: avatarUrl })
    .eq("id", user.id);
  if (updateError) throw updateError;

  return avatarUrl;
}
